/**
 * Patch rc-picker's dayjs generateConfig to handle foreign dayjs instances.
 *
 * The @idaho-aeyc/ui-kit bundles its own copy of dayjs inline, creating a
 * separate Dayjs class without the weekday/localeData plugins. When the UI
 * kit's EditableDatePicker passes these foreign instances to antd/rc-picker,
 * methods like .weekday() fail with "clone.weekday is not a function".
 *
 * This module registers the required dayjs plugins and wraps the affected
 * rc-picker helpers so that any foreign dayjs object is first converted to
 * a standard (app-level) dayjs instance before processing.
 */
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';

dayjs.extend(weekday);
dayjs.extend(localeData);

// eslint-disable-next-line @typescript-eslint/no-var-requires
const rcPickerDayjsConfig = require('rc-picker/es/generate/dayjs');

const generateConfig = rcPickerDayjsConfig.default || rcPickerDayjsConfig;
const StandardDayjs = dayjs().constructor;

function toStandardDayjs(date: unknown) {
  if (date && typeof date === 'object' && !(date instanceof StandardDayjs)) {
    return dayjs((date as { valueOf(): number }).valueOf());
  }
  return date;
}

const origGetWeekDay = generateConfig.getWeekDay;
generateConfig.getWeekDay = function getWeekDay(date: unknown) {
  return origGetWeekDay.call(this, toStandardDayjs(date));
};

const origSetDate = generateConfig.setDate;
generateConfig.setDate = function setDate(date: unknown, num: number) {
  return origSetDate.call(this, toStandardDayjs(date), num);
};

const origSetMonth = generateConfig.setMonth;
generateConfig.setMonth = function setMonth(date: unknown, num: number) {
  return origSetMonth.call(this, toStandardDayjs(date), num);
};

const origSetYear = generateConfig.setYear;
generateConfig.setYear = function setYear(date: unknown, num: number) {
  return origSetYear.call(this, toStandardDayjs(date), num);
};

const origGetEndDate = generateConfig.getEndDate;
generateConfig.getEndDate = function getEndDate(date: unknown) {
  return origGetEndDate.call(this, toStandardDayjs(date));
};
